import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Plus, Minus, Trash2 } from "lucide-react";
import WCButton from "@/components/WCButton";
import AnimateIn from "@/components/AnimateIn";

export default function Cart() {
  const { items, total, currency, count, updateLine, removeLine, goToCheckout } = useCart();

  return (
    <div className="bg-black min-h-screen pt-[160px] md:pt-[200px] pb-24 px-6" data-testid="cart-page">
      <div className="max-w-5xl mx-auto">
        <AnimateIn variant="fade">
          <h1 className="wc-heading text-white text-5xl md:text-7xl tracking-widest">
            CART <span className="text-white/40 text-3xl md:text-4xl">({count})</span>
          </h1>
        </AnimateIn>

        {items.length === 0 ? (
          <div className="mt-16 border border-white/10 p-16 text-center" data-testid="cart-empty">
            <div className="wc-label text-xs text-white/50 mb-6">YOUR CART IS EMPTY</div>
            <p className="text-white/60 mb-10">Nothing in the cart yet. Question everything.</p>
            <WCButton as="link" to="/shop">
              Shop Now
            </WCButton>
          </div>
        ) : (
          <div className="mt-14 grid md:grid-cols-[1fr_380px] gap-16">
            <div className="space-y-6" data-testid="cart-items">
              {items.map((i) => (
                <div
                  key={i.id}
                  className="flex gap-6 border-t border-white/10 pt-6"
                  data-testid={`cart-page-item-${i.id}`}
                >
                  <Link
                    to={`/shop/${i.product.handle}`}
                    className="w-28 h-28 md:w-36 md:h-36 bg-[#0a0a0a] shrink-0 overflow-hidden"
                  >
                    {i.product.image && (
                      <img
                        src={i.product.image}
                        alt={i.product.name}
                        className="wc-image w-full h-full object-cover"
                      />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/shop/${i.product.handle}`}
                      className="wc-heading text-white text-2xl md:text-3xl tracking-widest"
                    >
                      {i.product.name}
                    </Link>
                    <div className="wc-label text-xs text-white/50 mt-2">SIZE {i.size}</div>
                    <div className="text-white/70 text-sm mt-2">${i.product.price.toFixed(2)} each</div>
                    <div className="flex items-center gap-5 mt-5">
                      <div className="flex items-center border border-white/30">
                        <button
                          onClick={() => updateLine(i.id, i.quantity - 1)}
                          className="px-3 py-2 text-white hover:bg-white hover:text-black"
                          aria-label="Decrease"
                          data-testid={`cart-page-dec-${i.id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-4 wc-label text-sm text-white">{i.quantity}</span>
                        <button
                          onClick={() => updateLine(i.id, i.quantity + 1)}
                          className="px-3 py-2 text-white hover:bg-white hover:text-black"
                          aria-label="Increase"
                          data-testid={`cart-page-inc-${i.id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeLine(i.id)}
                        className="wc-label text-xs text-white/60 hover:text-white flex items-center gap-2"
                        data-testid={`cart-page-remove-${i.id}`}
                      >
                        <Trash2 size={12} />
                        REMOVE
                      </button>
                    </div>
                  </div>
                  <div className="wc-heading text-white text-xl md:text-2xl tracking-widest shrink-0">
                    ${i.subtotal.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            <aside className="border border-white/20 p-8 self-start" data-testid="cart-summary">
              <div className="wc-heading text-white text-2xl tracking-widest">SUMMARY</div>
              <div className="mt-8 space-y-3 text-sm">
                <div className="flex justify-between text-white/80">
                  <span>Subtotal</span>
                  <span>${total.toFixed(2)} {currency}</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Taxes</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>
              <div className="border-t border-white/20 mt-6 pt-6 flex justify-between wc-heading text-xl tracking-widest text-white">
                <span>TOTAL</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <button
                onClick={goToCheckout}
                className="wc-btn wc-btn--filled w-full mt-8"
                data-testid="cart-page-checkout"
              >
                Proceed To Checkout
              </button>
              <p className="text-white/40 text-xs mt-5 wc-label">
                Secure checkout powered by Shopify.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
